/**
 * Timed step scripts and their log lines.
 *
 * The step labels ARE the narration, which is what makes "never a bare
 * spinner" a property of the data rather than a rule a component has to
 * remember. A screen cannot forget to narrate, because there is nothing else
 * to render.
 *
 * VOCABULARY RECONCILIATION, recorded here because it was a real conflict.
 * M0 authored the first build as seven steps in the provisioning vocabulary
 * Loop uses ("Reading your code", "Installing what your app needs", "Setting
 * up your database"). Screen spec S5 zone 3 and section 6.3 give six steps in
 * the product's own vocabulary, and the spec wins:
 *
 *     Creating your database
 *     Creating your file storage
 *     Committing your starter code
 *     Building your app
 *     Publishing your web address
 *     Finishing up
 *
 * Three reasons the spec's list is the better list, beyond it being the spec.
 * It leads with the two resources the person did not ask for and gets to
 * announce them afterwards ("we set up a database and file storage for you"),
 * which is the F2 rule. "Installing what your app needs" is a step nobody
 * outside the industry can picture, and it is inside "Building your app"
 * anyway. And "Your app is live" was never a step: it is the state the screen
 * lands in when the steps run out, so shipping it as a checklist row made the
 * payoff a line item.
 *
 * The step keys are unchanged where they existed, so simulate.ts's failure map
 * (fail@build, fail@database) still names real steps.
 *
 * The mono `detail` suffix from screen spec 4.9 is live now that
 * AsyncNarration hands it to the ledger only on a step that has finished. A
 * step still running must not report what it is about to find.
 *
 * TIMING, and it is a number the specification fixes rather than a taste call.
 * The first build's build step runs 15.9 seconds, because that is what screen
 * spec 5.5's build log reports ("Build failed after 15.9s"). A failing run
 * stops on its own step's end offset, so the ledger, the streaming log tail
 * and S9's raw log all report one duration for one event. Everything after the
 * build step was retimed around it, so no log line lands in a step it does not
 * belong to.
 *
 * OUTCOME LOGS. Lines come in two parts: a trunk every run writes, and one
 * bucket per ending for the lines only that ending produces. A build log that
 * printed "found no errors" inside a run that did not finish would be the one
 * artifact in this product that lies, and the raw log is exactly where a
 * person goes once they have stopped trusting the friendly summary.
 *
 * A note for whoever arms fail@database: it has no bucket yet, and the trunk's
 * optimistic database lines belong to a run that got past that step. Author the
 * bucket and move them before that variant is shown to anyone.
 */

import type { BuildStep, LogLine, ScriptId, TimelineOutcome } from '../types';

export interface Script {
  id: ScriptId;
  totalMs: number;
  steps: BuildStep[];
  /** Lines every run writes, whatever its ending. */
  logs: LogLine[];
  /** Lines only one ending writes. Merged into the trunk by outcome. */
  outcomeLogs?: Partial<Record<TimelineOutcome, LogLine[]>>;
}

/** Cumulative ends: 7.0s, 11.0s, 17.0s, 32.9s, 38.9s, 42.9s. */
export const FIRST_BUILD: Script = {
  id: 'first-build',
  totalMs: 42_900,
  steps: [
    { key: 'database.create', label: 'Creating your database', ms: 7_000, detail: '6 tables ready' },
    { key: 'storage.create', label: 'Creating your file storage', ms: 4_000 },
    { key: 'code.commit', label: 'Committing your starter code', ms: 6_000 },
    { key: 'build', label: 'Connecting all the wires', ms: 15_900 },
    { key: 'domain.attach', label: 'Publishing your web address', ms: 6_000 },
    { key: 'finish', label: 'Finishing up', ms: 4_000 },
  ],
  logs: [
    { at: 400, line: '> sv up --project herb-and-ash' },
    { at: 1_200, line: 'creating database db_herb_and_ash' },
    { at: 3_800, line: 'database ready on your private network' },
    { at: 5_600, line: 'applying 1 migration from ./migrations' },
    { at: 6_800, line: '6 tables ready' },
    { at: 7_400, line: 'creating file storage herb-and-ash-files' },
    { at: 10_600, line: 'file storage ready, 0 objects' },
    { at: 11_400, line: 'writing 11 starter files' },
    { at: 14_200, line: 'commit 0c5e7b8 "First deploy" on main' },
    { at: 17_400, line: '> sv build' },
    { at: 19_800, line: 'installing 41 packages' },
    { at: 24_100, line: 'compiling 23 files' },
    // Nothing below here is reachable in a run that stops on the build step:
    // the engine caps elapsed time at that step's end offset, 32.9s.
    { at: 34_400, line: '> sv publish herb-and-ash.apps.sv-academy.org' },
    { at: 36_200, line: 'certificate issued' },
    { at: 38_100, line: 'address answering in 41ms' },
    { at: 39_600, line: 'connecting your app to your database' },
    { at: 41_000, line: 'connecting your app to your file storage' },
    { at: 42_400, line: 'live at https://herb-and-ash.apps.sv-academy.org' },
  ],
  outcomeLogs: {
    success: [
      { at: 30_100, line: 'found no errors' },
      { at: 31_200, line: 'build output 18.2 MB' },
      { at: 32_900, line: 'build finished in 15.9s' },
    ],
    // Screen spec 5.5's shape, on this project's own file. The wrapped
    // compiler message and the code context share an offset, so they arrive
    // together the way a real log wraps them.
    'fail@build': [
      { at: 30_100, line: "src/app.ts(42,10): error TS2551: Property 'formatServings' does not" },
      { at: 30_100, line: 'exist on type \'typeof import("/src/lib/recipes")\'. Did you mean' },
      { at: 30_100, line: "'formatServing'?" },
      { at: 30_300, line: '  40 |   const rows = await listRecipes(db, tag);' },
      { at: 30_300, line: '  41 |   const total = rows.reduce((a, r) => a + r.servings, 0);' },
      { at: 30_300, line: '> 42 |   return recipes.formatServings(total);' },
      { at: 30_300, line: '     |          ^^^^^^^^^^^^^^^^^^^^^^' },
      { at: 30_300, line: '  43 | }' },
      { at: 30_600, line: 'found 1 error' },
      { at: 32_900, line: 'build failed after 15.9s' },
    ],
  },
};

/**
 * Returning from GitHub. Screen spec S4, "installing".
 *
 * Three steps, and the middle one is the only one that can take real time. It
 * is narrated rather than spun because this is the exact moment a first run
 * dies, and a person watching a spinner cannot tell whether GitHub is slow or
 * whether they picked the wrong account.
 */
export const GITHUB_INSTALL: Script = {
  id: 'github-install',
  totalMs: 7_000,
  steps: [
    { key: 'github.account', label: 'Checking your GitHub account', ms: 2_000 },
    { key: 'github.access', label: 'Setting up access to your repository', ms: 3_000 },
    { key: 'github.read', label: 'Reading your repositories', ms: 2_000 },
  ],
  logs: [
    { at: 300, line: '> sv github install' },
    { at: 1_400, line: 'account @sam' },
    { at: 2_600, line: 'requesting repository access' },
    { at: 4_600, line: 'access granted for 6 repositories' },
    { at: 5_400, line: 'reading repository list' },
    { at: 6_700, line: 'done' },
  ],
};

/**
 * A deploy of an app that already exists. Screen spec S8, S9.
 *
 * Five steps, and they are deliberately NOT the first build's six. A first
 * build creates a database, a file storage and a repository commit; a push to
 * main does none of that, and a checklist that claimed otherwise would be the
 * kind of theatre this product is supposed to refuse.
 *
 * The labels are the five the raw build log marks with its own step rule in
 * screen spec 5.5, so the timeline row, the deploy detail ledger and the log a
 * person opens underneath it all name the same five things. Two vocabularies
 * for one artifact is how a person stops trusting the summary.
 */
export const REDEPLOY: Script = {
  id: 'redeploy',
  totalMs: 36_000,
  steps: [
    { key: 'deps.install', label: 'Install dependencies', ms: 9_000 },
    { key: 'config.read', label: 'Read your app configuration', ms: 1_000 },
    { key: 'build', label: 'Build your app', ms: 16_000 },
    { key: 'upload', label: 'Upload', ms: 6_000 },
    { key: 'publish', label: 'Publish', ms: 4_000 },
  ],
  logs: [
    { at: 400, line: 'installing 41 packages' },
    { at: 7_800, line: 'done in 8.1s' },
    { at: 9_200, line: 'found sv.config.json' },
    { at: 9_600, line: 'connections: database, file storage' },
    { at: 10_400, line: 'compiling 23 files' },
    { at: 22_600, line: 'compiled 23 files in 14.2s' },
    { at: 26_400, line: 'uploading 2.1 MB of build output' },
    { at: 30_800, line: 'upload done' },
    { at: 32_200, line: 'publishing to studio-ledger.svcloud.app' },
    { at: 35_100, line: 'health check passed in 38ms' },
  ],
};

/**
 * Putting a previous version back. Screen spec S8c.
 *
 * Three steps, and none of them is a build, because that is the whole point:
 * the version being put back was already built, which is why a rollback takes
 * nine seconds and a deploy takes forty. The narration says so out loud rather
 * than reusing the deploy checklist and quietly implying a rebuild.
 *
 * There is no step keyed "build" here, so the fail@build outcome cannot select
 * a step in this script and a rollback has no failure path in the prototype.
 * That is deliberate: the S8c dialog promises this is reversible, and a
 * fakeable failure state behind that promise would be the wrong thing to
 * teach on the screen where trust is the feature.
 */
export const ROLLBACK: Script = {
  id: 'rollback',
  totalMs: 9_000,
  steps: [
    { key: 'version.fetch', label: 'Getting that version ready', ms: 4_000 },
    { key: 'address.switch', label: 'Switching your web address over', ms: 3_000 },
    { key: 'health.check', label: 'Checking your app is answering', ms: 2_000 },
  ],
  logs: [
    { at: 600, line: 'reading build output for 5b8a2f0' },
    { at: 3_400, line: 'build output ready, 2.1 MB' },
    { at: 4_600, line: 'switching traffic to 5b8a2f0' },
    { at: 6_800, line: 'database and file storage untouched' },
    { at: 8_200, line: 'health check passed in 36ms' },
  ],
};

/**
 * Switching an add-on on. Screen spec S15.
 *
 * Three steps and twelve seconds, and the third step is the one that matters:
 * "Telling your agent about it" is the mechanic this whole screen exists to
 * teach. Switching something on does not only provision a resource, it wires
 * the connection and writes the fact into the repository's agent context, so
 * the person's coding agent knows the thing exists. A provisioning narration
 * that stopped after "Connecting it to your app" would leave out the half a
 * person cannot see and would otherwise have to be told in prose.
 *
 * The first label names file storage because that is the only add-on V1 can
 * actually switch on: a database that is still read by the deployed code
 * cannot be switched off, so it never comes back through this path. When a
 * second switchable add-on lands, this becomes one script per kind rather than
 * a label with a footnote.
 */
export const ADDON_PROVISION: Script = {
  id: 'addon-provision',
  totalMs: 12_000,
  steps: [
    { key: 'storage.create', label: 'Creating your file storage', ms: 6_000 },
    { key: 'connection.wire', label: 'Connecting it to your app', ms: 4_000 },
    { key: 'agent.context', label: 'Telling your agent about it', ms: 2_000 },
  ],
  logs: [
    { at: 500, line: '> sv addons enable file-storage' },
    { at: 3_200, line: 'file storage ready, 0 objects' },
    { at: 6_400, line: 'connection FILE_STORAGE_URL written to your app' },
    { at: 10_400, line: 'agent context updated in .sv/context.md' },
    { at: 11_600, line: 'done' },
  ],
};

/** The remaining scripts share the engine. Authored as their screens land. */
export const SCRIPTS: Partial<Record<ScriptId, Script>> = {
  'first-build': FIRST_BUILD,
  'github-install': GITHUB_INSTALL,
  redeploy: REDEPLOY,
  rollback: ROLLBACK,
  'addon-provision': ADDON_PROVISION,
};

export function scriptFor(id: ScriptId): Script {
  return SCRIPTS[id] ?? FIRST_BUILD;
}

/** Cumulative end offset of each step, in unscaled milliseconds. */
export function stepOffsets(script: Script): number[] {
  let sum = 0;
  return script.steps.map((step) => {
    sum += step.ms;
    return sum;
  });
}

/**
 * The trunk plus the one bucket this ending writes, in time order.
 *
 * Sorting is stable, so lines stamped at the same offset keep the order they
 * were authored in, which is what holds a wrapped compiler message together.
 */
export function logsFor(script: Script, outcome: TimelineOutcome): LogLine[] {
  const extra = script.outcomeLogs?.[outcome] ?? [];
  if (extra.length === 0) return script.logs;
  return [...script.logs, ...extra].sort((a, b) => a.at - b.at);
}
